import type { BrowserOptions } from '@sentry/browser';
import {
  browserTracingIntegration,
  getCurrentScope,
  init,
  setContext,
  setTag,
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage,
} from '@sentry/browser';
import type { Attachment, CaptureContext } from '@sentry/types';
import type { ZodError } from 'zod';

import {
  APP_ENV,
  CHECKOUT_API_BASE_URL,
  CORE_API_BASE_URL,
  SENTRY_TRACES_SAMPLE_RATE,
  VERSION,
} from '@chk/env-variables';

import type { SentryContextProps, SentryInitProps } from './sentry.types';
import { isValidObject, sentrySanitizeUrl } from './sentry.utils';

const DEFAULT_SENTRY_CONFIG = {
  release: VERSION,
  integrations: [browserTracingIntegration()],
  // target only backend services that also allow headers in CORS: "sentry-trace", "baggage"
  tracePropagationTargets: [
    sentrySanitizeUrl(CORE_API_BASE_URL),
    sentrySanitizeUrl(CHECKOUT_API_BASE_URL),
  ],
  tracesSampler: () => {
    // for further customization of sampling rate
    return SENTRY_TRACES_SAMPLE_RATE || 0;
  },
  ignoreErrors: [
    'top.GLOBALS', // NOTE: ignores random plugins/extensions,
    'Non-Error exception captured',
    `Failed to set the 'href' property on 'Location'`,
    `Blocked a frame with origin`,
  ],
  normalizeDepth: 4,
  maxValueLength: 500,
  allowUrls: [
    /https?:\/\/([a-z0-9]+[.])*volt[.]io/,
    /https?:\/\/([a-z0-9]+[.])*globalpayapi[.]com/,
  ],
} satisfies BrowserOptions;

export const initErrorTracking = ({ dsn, options }: SentryInitProps) => {
  const environment = APP_ENV;

  if (!dsn || environment === 'dev') {
    return;
  }

  init({
    ...DEFAULT_SENTRY_CONFIG,
    ...options,
    dsn,
    environment,
  });
};

export const setSentryContext = ({ key, val }: SentryContextProps): void => {
  if (isValidObject(val)) {
    setContext(key, val);
  }
};

export const addSentryAttachment = (attachment: Attachment) => {
  getCurrentScope().addAttachment(attachment);
};

export const setSentryTags = <T extends Record<string, string>>(tags: T) => {
  Object.entries(tags).forEach(([tagKey, tagValue]) =>
    setTag(tagKey, tagValue)
  );
};

export const captureException = (
  error: unknown,
  captureContext?: CaptureContext
): void => {
  if (typeof error === 'string') {
    sentryCaptureException(new Error(error), captureContext);
    return;
  }

  sentryCaptureException(error, captureContext);
};

export const captureSchemaValidationError = (
  error: ZodError,
  captureContext?: CaptureContext
): void => {
  sentryCaptureException(error, captureContext);
};

export const captureMessage = (message: string): void => {
  sentryCaptureMessage(message);
};
